import { css } from '@emotion/react';
import {
  ActionIcon,
  Box,
  COLOR_PALETTE,
  Flex,
  Icon,
  Image,
  Text,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { FormattedTime, useLang } from '@inflearn/i18n-tools';
import { faChevronRight } from '@inflearn/pro-regular-svg-icons';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import { UCC_URL } from '../../../../constants/urls';
import { generateImageSrc } from '../../../../utils/generateImageSrc';
import { NoteCount } from './NoteCount';

const THUMBNAIL_WIDTH = 80;
const THUMBNAIL_HEIGHT = 52;

interface NoteItemProps {
  title: string;
  thumbnailSrc: string;
  accessedAt: string;
  id: number;
  isPublic: boolean;
  noteCount: number;
}

export const NoteItem = ({
  title,
  id,
  accessedAt,
  isPublic,
  noteCount,
  thumbnailSrc,
}: NoteItemProps) => {
  const { t } = useTranslation();
  const { lang } = useLang();

  return (
    <Flex
      w="100%"
      component={Link}
      href={UCC_URL.NOTES_ID(id)}
      align="center"
      justify="space-between"
      styles={{
        root: styleNoteItem,
      }}
    >
      <Flex gap={16} align="center">
        <Box component="picture" css={styleThumbnail}>
          <Image
            width={THUMBNAIL_WIDTH}
            height={THUMBNAIL_HEIGHT}
            src={generateImageSrc(thumbnailSrc, THUMBNAIL_WIDTH)}
            alt={t('강의 노트 - {{title}}', { title: title })}
          />
        </Box>
        <Box>
          <Text fw={600} c="gray.9" lineClamp={1}>
            {title}
          </Text>
          <Flex gap={4} align="center">
            <NoteCount count={noteCount} />
            <Text
              fz={14}
              c="gray.6"
              styles={{
                root: styleDivider,
              }}
            >
              {isPublic ? t('공개') : t('비공개')}
            </Text>
            <Text
              fz={14}
              c="gray.6"
              styles={{
                root: {
                  ...styleDivider,
                  [desktopFirstMediaQuery.mobile]: { display: 'none' },
                },
              }}
            >
              {t('최근 학습일')} <FormattedTime dateTime={accessedAt} locale={lang} />
            </Text>
          </Flex>
        </Box>
      </Flex>
      <ActionIcon
        variant="transparent"
        color="dark.9"
        size="n-sm"
        styles={{
          root: { flex: '0 0 auto' },
        }}
      >
        <Icon icon={faChevronRight} />
      </ActionIcon>
    </Flex>
  );
};

const styleNoteItem = css({
  cursor: 'pointer',
  padding: 16,
  gap: 24,

  '@media(hover: hover)': {
    '&:hover': {
      backgroundColor: COLOR_PALETTE.gray[0],
    },
  },

  [desktopFirstMediaQuery.mobile]: {
    padding: '16px 0 16px 4px',
    gap: 8,
  },
});

const styleThumbnail = css({
  borderRadius: 8,
  border: `1px solid ${COLOR_PALETTE.gray[2]}`,
  width: THUMBNAIL_WIDTH,
  height: THUMBNAIL_HEIGHT,
  overflow: 'hidden',
  flex: `0 0 ${THUMBNAIL_WIDTH}px`,

  [desktopFirstMediaQuery.mobile]: {
    display: 'none',
  },
});

const styleDivider = css({
  ':before': {
    content: '"･"',
    fontSize: 14,
    color: COLOR_PALETTE.gray[6],
    marginRight: 4,
  },
});
